// eslint-disable-next-line no-unused-vars
import { FC, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import RESOURCES from '~/resources';
import { KEYBOARD_TYPES } from '~/data/constants';
import { ARIA_LABELS_RESOURCES } from '~/resources/resources';
import Container from '../Container';
import {
  InputsWrapper,
  ProgressBarStyle,
  sliderStyle,
  useStyles,
  valueStyle,
} from './useStyles';
import CustomTextField from '../CustomTextField';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { debounce } from 'lodash';
import { Divider, SxProps } from '@mui/material';

interface IRangePriceBarProps {
  minPriceState: number | null;
  maxPriceState: number | null;
  maxRangePrice: number;
  minRangePrice: number;
  step: number;
  style?: React.CSSProperties | SxProps;
  minDistance: number;
  onChange: (pricaRange: number[]) => void;
  label?: string;
}

export const RangePriceBar: FC<IRangePriceBarProps> = ({
  maxRangePrice,
  minRangePrice,
  minPriceState,
  maxPriceState,
  step,
  minDistance,
  style = {},
  onChange,
  label,
}) => {
  const classes = useStyles();
  const ref = useRef<HTMLSpanElement | null>(null);

  const [value, setValue] = useState({
    minPrice: Number(minPriceState || minRangePrice),
    maxPrice: Number(maxPriceState || maxRangePrice),
  });

  const theme = createTheme({
    // for Slider RTL direction
    direction: 'rtl',
  });

  useEffect(() => {
    setValue({
      minPrice: Number(minPriceState || minRangePrice),
      maxPrice: Number(maxPriceState || maxRangePrice),
    });
  }, [maxPriceState, maxRangePrice, minPriceState, minRangePrice]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceDispatch = useMemo(
    () =>
      debounce((newValue: number[]) => {
        let [minPrice, maxPrice] = newValue;
        minPrice = Math.max(minPrice, minRangePrice);
        maxPrice = Math.min(maxPrice, maxRangePrice);

        if (maxPrice < minPrice)
          maxPrice = Math.min(minPrice + minDistance, maxRangePrice);
        if (minPrice > maxPrice)
          minPrice = Math.max(maxPrice - minDistance, minRangePrice);

        setValue({ minPrice, maxPrice });
        onChange([minPrice, maxPrice]);
      }, 500),
    [maxRangePrice, minDistance, minRangePrice, onChange],
  );

  useEffect(() => {
    return () => {
      debounceDispatch.cancel();
    };
  }, [debounceDispatch]);

  const handleChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    if (activeThumb === 0) {
      setValue({
        minPrice: Math.min(newValue[0], value.maxPrice - minDistance),
        maxPrice: value.maxPrice,
      });
    } else {
      setValue({
        minPrice: value.minPrice,
        maxPrice: Math.max(newValue[1], value.minPrice + minDistance),
      });
    }
  };

  const handleArrowsKeyDown = useCallback(
    (
      event: { preventDefault: () => void; key: string },
      leftArrowNewValue: { minPrice: number; maxPrice: number },
      rightArrowNewValue: { minPrice: number; maxPrice: number },
    ) => {
      if (event.key === KEYBOARD_TYPES.ARROW_LEFT) {
        event.preventDefault();
        setValue(leftArrowNewValue);
        onChange([leftArrowNewValue.minPrice, leftArrowNewValue.maxPrice]);
      } else if (event.key === KEYBOARD_TYPES.ARROW_RIGHT) {
        event.preventDefault();
        setValue(rightArrowNewValue);
        onChange([rightArrowNewValue.minPrice, rightArrowNewValue.maxPrice]);
      }
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: { preventDefault: () => void; key: string }) => {
      const focusedSliderElement =
        document.activeElement?.getAttribute('data-index');
      // focusedSliderElement 0 MIN, 1 MAX
      if (ref && ref.current) {
        if (focusedSliderElement && focusedSliderElement === '0') {
          handleArrowsKeyDown(
            event,
            {
              minPrice: Math.min(
                value.maxPrice - minDistance,
                value.minPrice + minDistance,
              ),
              maxPrice: value.maxPrice,
            },
            {
              minPrice: Math.max(minRangePrice, value.minPrice - minDistance),
              maxPrice: value.maxPrice,
            },
          );
        }
        if (focusedSliderElement && focusedSliderElement === '1') {
          handleArrowsKeyDown(
            event,
            {
              minPrice: value.minPrice,
              maxPrice: Math.min(maxRangePrice, value.maxPrice + minDistance),
            },
            {
              minPrice: value.minPrice,
              maxPrice: Math.max(
                value.minPrice + minDistance,
                value.maxPrice - minDistance,
              ),
            },
          );
        }
      }
    },
    [
      handleArrowsKeyDown,
      maxRangePrice,
      minDistance,
      minRangePrice,
      value.maxPrice,
      value.minPrice,
    ],
  );

  const getAriaLabel = (index: number) => {
    if (index === 0) {
      return `${ARIA_LABELS_RESOURCES.MINIMUM_PRICE} ${label || ''}`;
    }
    if (index === 1) {
      return `${ARIA_LABELS_RESOURCES.MAXIMUM_PRICE} ${label || ''}`;
    }
    return '';
  };

  const isContainsDigitsOnly = (inputValue: string) => {
    const sanitizedValue = inputValue.replace(/,/g, '') || '';
    return /^\d+$/.test(sanitizedValue);
  };

  const handleMinPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newMinPrice;
    if (!isContainsDigitsOnly(event.target.value || '')) {
      return;
    }
    if (!event.target.value) {
      newMinPrice = minRangePrice;
      return;
    }
    newMinPrice = parseInt(event.target.value.replace(/\D/g, ''), 10);
    if (newMinPrice > maxRangePrice) {
      newMinPrice = maxRangePrice;
    } else if (newMinPrice > value.maxPrice) {
      setValue({ minPrice: newMinPrice, maxPrice: newMinPrice });
      debounceDispatch([newMinPrice, newMinPrice]);
    } else {
      setValue({ minPrice: newMinPrice, maxPrice: value.maxPrice });
      debounceDispatch([newMinPrice, value.maxPrice]);
    }
  };

  const handleMaxPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newMaxPrice;
    if (!isContainsDigitsOnly(event.target.value || '')) {
      return;
    }
    if (!event.target.value) {
      newMaxPrice = 0;
      return;
    }
    newMaxPrice = parseInt(event.target.value.replace(/\D/g, ''), 10);
    setValue({ minPrice: value.minPrice, maxPrice: newMaxPrice });
    debounceDispatch([value.minPrice, newMaxPrice]);
  };

  return (
    <Container sx={{ ...classes.budgetInputsContainer, ...style }}>
      <InputsWrapper>
        <CustomTextField
          onChange={handleMinPriceChange}
          value={value.minPrice.toLocaleString()}
          label={RESOURCES.MIN}
          customStyle={valueStyle}
          isPortalStyle={true}
        />
        <Divider sx={classes.divider} />
        <CustomTextField
          onChange={handleMaxPriceChange}
          value={value.maxPrice.toLocaleString()}
          label={RESOURCES.MAX}
          customStyle={valueStyle}
          isPortalStyle={true}
        />
      </InputsWrapper>
      <ThemeProvider theme={theme}>
        <ProgressBarStyle
          value={[value.minPrice, value.maxPrice]}
          valueLabelFormat={(value) => value.toLocaleString()}
          onChange={handleChange}
          onChangeCommitted={() => onChange([value.minPrice, value.maxPrice])}
          valueLabelDisplay="auto"
          disableSwap
          min={minRangePrice}
          max={maxRangePrice}
          step={step}
          sx={sliderStyle}
          onKeyDown={handleKeyDown}
          ref={ref}
          getAriaLabel={getAriaLabel}
          getAriaValueText={(currentValue) =>
            `${currentValue} ${ARIA_LABELS_RESOURCES.SHEKEL}`
          }
        />
      </ThemeProvider>
    </Container>
  );
};
